import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface MobileListProps {
	children: ReactNode;
	/** A rounded card inset from the screen edges (settings style) instead of edge to edge. */
	inset?: boolean;
	className?: string;
	'aria-label'?: string;
}

/**
 * A list of MobileListRows with iOS hairline dividers that start at the text,
 * not the screen edge. Rows may be wrapped (e.g. in a SwipeableRow).
 */
export function MobileList({ children, inset = false, className, 'aria-label': ariaLabel }: MobileListProps) {
	return (
		<div
			role="list"
			aria-label={ariaLabel}
			className={cn(
				'[&>*:last-child_[data-row-body]]:border-b-0',
				// Rows paint --row-bg (opaque, so a SwipeableRow's actions stay hidden).
				inset &&
					'mx-4 overflow-hidden rounded-xl bg-background ring-1 ring-border [--row-bg:hsl(var(--background))] dark:bg-zinc-900 dark:ring-white/10 dark:[--row-bg:theme(colors.zinc.900)]',
				className,
			)}
		>
			{children}
		</div>
	);
}

/** A titled MobileList with an optional footer note. */
export function MobileListSection({
	title,
	footer,
	children,
	inset = true,
	className,
}: {
	title?: ReactNode;
	footer?: ReactNode;
	children: ReactNode;
	inset?: boolean;
	className?: string;
}) {
	return (
		<section className={cn('mt-6 first:mt-3', className)}>
			{title && (
				<h2 className="px-8 pb-1.5 text-[13px] font-normal uppercase tracking-wide text-muted-foreground">{title}</h2>
			)}
			<MobileList inset={inset}>{children}</MobileList>
			{footer && <p className="px-8 pt-1.5 text-[13px] text-muted-foreground">{footer}</p>}
		</section>
	);
}

interface MobileListRowProps {
	/** Avatar, icon tile or checkbox. */
	leading?: ReactNode;
	title: ReactNode;
	/** One or two lines under the title. */
	subtitle?: ReactNode;
	/** Small text right of the title: a time, an amount. */
	meta?: ReactNode;
	/** Right side under the meta or on its own: a badge, a value, a switch. */
	trailing?: ReactNode;
	/** Shows a disclosure chevron. Defaults to true for rows that navigate. */
	chevron?: boolean;
	/** Navigates with react-router. */
	to?: string;
	onClick?: () => void;
	selected?: boolean;
	/** Bold title, for unread items. */
	emphasized?: boolean;
	disabled?: boolean;
	className?: string;
	'aria-label'?: string;
}

/**
 * A phone list row: 44px minimum (about 64px with a subtitle), the whole row
 * is the tap target with press feedback, and the content truncates instead
 * of wrapping the layout. Renders a Link with `to`, a button with `onClick`,
 * and a plain row otherwise.
 */
export function MobileListRow({
	leading,
	title,
	subtitle,
	meta,
	trailing,
	chevron,
	to,
	onClick,
	selected = false,
	emphasized = false,
	disabled = false,
	className,
	'aria-label': ariaLabel,
}: MobileListRowProps) {
	const interactive = Boolean(to || onClick);
	const showChevron = chevron ?? Boolean(to);
	const body = (
		<>
			{leading && <div className="flex shrink-0 items-center py-2.5">{leading}</div>}
			<div
				data-row-body
				className="flex min-h-11 min-w-0 flex-1 items-center gap-2 border-b border-border/70 py-2.5 pr-4"
			>
				<div className="min-w-0 flex-1">
					<div className="flex items-baseline gap-2">
						<div className={cn('min-w-0 flex-1 truncate text-[16px] leading-snug', emphasized ? 'font-semibold' : 'font-medium')}>
							{title}
						</div>
						{meta && <div className="shrink-0 text-[13px] tabular-nums text-muted-foreground">{meta}</div>}
					</div>
					{subtitle && <div className="line-clamp-2 text-[14px] leading-snug text-muted-foreground">{subtitle}</div>}
				</div>
				{trailing && <div className="flex shrink-0 items-center">{trailing}</div>}
				{showChevron && <ChevronRight aria-hidden className="size-5 shrink-0 text-muted-foreground/50" />}
			</div>
		</>
	);
	const rowClass = cn(
		'flex w-full min-w-0 items-stretch gap-3 bg-[color:var(--row-bg,hsl(var(--background)))] pl-4 text-left',
		interactive && 'select-none active:bg-muted/70 dark:active:bg-zinc-800',
		selected && 'bg-muted/60',
		disabled && 'pointer-events-none opacity-50',
		className,
	);

	if (to) {
		return (
			<div role="listitem">
				<Link to={to} aria-label={ariaLabel} aria-current={selected ? 'page' : undefined} className={rowClass}>
					{body}
				</Link>
			</div>
		);
	}
	if (onClick) {
		return (
			<div role="listitem">
				<button type="button" onClick={onClick} aria-label={ariaLabel} disabled={disabled} className={rowClass}>
					{body}
				</button>
			</div>
		);
	}
	return (
		<div role="listitem" aria-label={ariaLabel} className={rowClass}>
			{body}
		</div>
	);
}
